// react
import React from 'react';

// material ui
import {
  alpha,
  Avatar,
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';

// shared components
import PercentageTrendChip from '@/components/ui/chip/PercentageTrendChip';

// components
// utils
import { formatNumber } from '@/utils/formatters';

interface EnergyKpiCardProps {
  title: string;
  icon: React.ReactElement;
  color: string;

  value?: number;
  unit: string;

  period?: string;
  trend?: number;
  trendTooltip?: string;

  enabled?: boolean;
  unavailableText?: string;
  noDataText?: string;

  onClick?: () => void;

  actions?: React.ReactNode;
  children?: React.ReactNode;
}

const EnergyKpiCard = ({
  title,
  icon,
  color,
  value,
  unit,
  period,
  trend,
  trendTooltip,
  enabled = true,
  unavailableText,
  noDataText,
  onClick,
  actions,
  children,
}: EnergyKpiCardProps) => {
  const isClickable = enabled && typeof onClick === 'function';

  const content = (
    <CardContent
      sx={{
        height: '100%',
        p: 2,
        '&:last-child': { pb: 2 },
      }}
    >
      <Stack spacing={1.5} height="100%">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1.5}
        >
          <Box display="flex" alignItems="center" gap={1.25} minWidth={0}>
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: alpha(color, 0.1),
                borderRadius: 2,
                width: 36,
                height: 36,
              }}
            >
              {React.cloneElement(icon, {
                sx: {
                  verticalAlign: 'middle',
                  color,
                  fontSize: 20,
                },
              })}
            </Avatar>

            <Typography variant="subtitle2" fontWeight={700} noWrap>
              {title}
            </Typography>
          </Box>

          {actions ? (
            <Box
              display="flex"
              alignItems="center"
              flexShrink={0}
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
            >
              {actions}
            </Box>
          ) : null}
        </Box>

        {!enabled ? (
          <Typography variant="caption" color="text.secondary">
            {unavailableText}
          </Typography>
        ) : value == null ? (
          <Typography variant="caption" color="text.secondary">
            {noDataText}
          </Typography>
        ) : (
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Box display="flex" alignItems="baseline" gap={1}>
                <Typography variant="h5" fontWeight={700}>
                  {formatNumber(value)}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {unit}
                </Typography>
              </Box>

              {trend != null && (
                <PercentageTrendChip
                  value={trend}
                  tooltip={trendTooltip}
                  showArrow
                />
              )}
            </Box>

            {period && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  display: 'block',
                }}
              >
                {period}
              </Typography>
            )}
          </Box>
        )}

        {enabled && children}
      </Stack>
    </CardContent>
  );

  return (
    <Card
      variant="outlined"
      onClick={isClickable ? onClick : undefined}
      onKeyDown={(event) => {
        if (!isClickable) {
          return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      sx={{
        opacity: enabled ? 1 : 0.5,
        height: '100%',
        cursor: isClickable ? 'pointer' : 'default',
      }}
    >
      {content}
    </Card>
  );
};

export default EnergyKpiCard;
